import { useMemo, useState } from "react";
import Button from "@mui/material/Button";
import { getGoogleMapsApiKey, loadGoogleMaps } from "@bw/shared/components";
import type { PublicFacilityResponse } from "@bw/shared/types/generated";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { MaterialFacilityMap } from "../../../common/components/MaterialFacilityMap";

/** Public venue coordinates only; no discovery actions or device location. */
export function FacilityLocation({
  facility,
}: {
  facility: PublicFacilityResponse;
}) {
  const [copyStatus, setCopyStatus] = useState("");
  const address = facility.address
    ? [
        facility.address.line1,
        facility.address.line2,
        facility.address.city,
        facility.address.state,
        facility.address.postal_code,
      ]
        .filter(Boolean)
        .join(", ")
    : facility.location;
  const lat = facility.address?.latitude;
  const lng = facility.address?.longitude;
  const hasCoords =
    typeof lat === "number" &&
    Number.isFinite(lat) &&
    typeof lng === "number" &&
    Number.isFinite(lng);
  const facilities = useMemo(
    () =>
      hasCoords
        ? [
            {
              facility_id: facility.facility_id,
              name: facility.name,
              latitude: lat,
              longitude: lng,
            },
          ]
        : [],
    [facility.facility_id, facility.name, hasCoords, lat, lng],
  );
  const center = useMemo(
    () => (hasCoords ? { latitude: lat, longitude: lng } : null),
    [hasCoords, lat, lng],
  );
  return (
    <Stack component="section" aria-label="Location" spacing={1.5}>
      <Typography component="h2" sx={{ fontSize: "1rem", fontWeight: 700 }}>
        Location
      </Typography>
      <Typography>{address}</Typography>
      <Stack direction="row" spacing={1}>
        <Button
          component="a"
          size="small"
          target="_blank"
          rel="noopener noreferrer"
          href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`}
        >
          Get directions
        </Button>
        <Button
          size="small"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(address);
              setCopyStatus("Address copied");
            } catch {
              setCopyStatus(
                "Could not copy. Select the address above to copy it.",
              );
            }
          }}
        >
          Copy address
        </Button>
      </Stack>
      {copyStatus ? (
        <Typography role="status" variant="body2">
          {copyStatus}
        </Typography>
      ) : null}
      {center ? (
        <MaterialFacilityMap
          mode="location"
          hideUnavailable
          facilities={facilities}
          center={center}
          getGoogleMapsApiKey={getGoogleMapsApiKey}
          loadGoogleMaps={loadGoogleMaps}
        />
      ) : null}
    </Stack>
  );
}
